import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { artists as _seedArtists } from '../mockData';
import { useArtists, useGallery } from '../lib/usePublicData';
import { useT } from '../lib/i18n';

// Elegant monogram fallback when no artist photo is provided
const Monogram = ({ name }) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-rich-green via-dark-green to-charcoal relative">
      <div className="absolute inset-4 border-2 border-gold/40"></div>
      <div className="absolute inset-6 border border-gold/20"></div>
      <span
        className="text-7xl text-gold tracking-widest relative z-10"
        style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.15em' }}
      >
        {initials}
      </span>
    </div>
  );
};

const Artists = () => {
  const { t } = useT();
  const { items: artists } = useArtists();
  const { items: gallery } = useGallery();
  const getWorksByArtist = (slug) => {
    const a = artists.find((x) => x.slug === slug);
    if (!a) return [];
    return gallery.filter((w) => (w.artist || '').trim() === a.name.trim());
  };
  return (
    <div className="min-h-screen bg-cream">
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-ivory">
        <div className="absolute inset-0 opacity-[0.05] deco-pattern"></div>
        <div className="relative z-10 text-center px-6">
          <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-6 border-2 border-dark-crimson"></div>
          <h1
            className="text-7xl font-bold text-dark-green mb-6"
            style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}
          >
            {t('artists.title')}
          </h1>
          <div className="h-1 w-40 bg-dark-crimson mx-auto mt-6 mb-6"></div>
          <p
            className="text-dark-brown text-2xl max-w-2xl mx-auto"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('artists.subtitle')}
          </p>
        </div>
      </section>

      {/* Artists Grid */}
      <section className="py-20 px-6 bg-cream deco-pattern">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {artists.map((artist) => {
              const works = getWorksByArtist(artist.slug);
              const representativeWork = works[0];
              return (
                <Link
                  key={artist.slug}
                  to={`/artists/${artist.slug}`}
                  data-testid={`artist-card-${artist.slug}`}
                  className="group block"
                >
                  <Card className="bg-ivory border-2 border-gold/30 overflow-hidden hover:border-dark-crimson transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 h-full">
                    <div className="relative h-72 overflow-hidden bg-charcoal">
                      {artist.photo ? (
                        <img
                          src={artist.photo}
                          alt={artist.name}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                        />
                      ) : representativeWork ? (
                        <img
                          src={representativeWork.image}
                          alt={`Work by ${artist.name}`}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700 opacity-90"
                        />
                      ) : (
                        <Monogram name={artist.name} />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-green/90 via-dark-green/20 to-transparent pointer-events-none"></div>
                      <div className="absolute bottom-4 left-6 right-6">
                        <p className="text-gold text-xs font-bold tracking-[0.25em] mb-1">
                          {artist.nationality?.toUpperCase()}
                        </p>
                        <h3
                          className="text-3xl font-bold text-cream"
                          style={{ fontFamily: 'Playfair Display, serif' }}
                        >
                          {artist.name}
                        </h3>
                      </div>
                    </div>
                    <div className="p-8 bg-ivory">
                      <p
                        className="text-charcoal leading-relaxed mb-4"
                        style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem' }}
                      >
                        {artist.shortBio}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t-2 border-gold/30">
                        <span
                          className="text-dark-crimson font-semibold"
                          style={{ fontFamily: 'Cormorant Garamond, serif' }}
                        >
                          {works.length} {works.length === 1 ? t('artists.workAvailable') : t('artists.worksAvailable')}
                        </span>
                        <span
                          className="text-dark-green font-bold group-hover:text-dark-crimson transition-colors"
                          style={{ fontFamily: 'Cormorant Garamond, serif' }}
                        >
                          View →
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Artists;
